import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "../useTitle";
import { Board, Loading, Notice, PageHead, Row } from "../components/Look";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  api,
  atLeast,
  roleName,
  type Member,
  type Project,
  type Role,
  type TestSelection,
} from "../api";
import { MarkdownEditor, MarkdownView } from "../components/Markdown";
import TestSelectionsEditor from "../components/TestSelectionsEditor";
import Members from "../components/Members";
import { useIsAdmin } from "../session";

/** The layer above benches: SNCF, IPI6, whatever comes next. */
export function Projects() {
  const { t } = useTranslation();
  useTitle(t("projects.title"));
  const [loaded, setLoaded] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  // Creating a project is an instance-level act; the button is hidden rather
  // than offered and refused.
  const admin = useIsAdmin();

  const load = useCallback(async () => {
    try {
      setProjects((await api.projects()).projects);
      setLoaded(true);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <PageHead
        title={t("projects.heading")}
        answer={
          projects.length > 0
            ? [t("projects.projectCount", { count: projects.length }),
               t("projects.benchCount", { count: projects.reduce((n, p) => n + p.bench_count, 0) }),
               t("projects.runCount", { count: projects.reduce((n, p) => n + p.run_count, 0) })].join(", ") + "."
            : undefined
        }
        actions={admin && (
          <button className="btn-primary" onClick={() => setCreating(true)}>
            {t("projects.newProject")}
          </button>
        )}
      />
      {!loaded && <Loading label={t("common.loading")} />}

      {error && <Notice tone="error">{error}</Notice>}

      {creating && (
        <form
          className="mb-4 flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.createProject(name.trim(), "");
              setName("");
              setCreating(false);
              await load();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            }
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("projects.namePlaceholder")}
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          />
          <button className="rounded-md bg-slate-900 px-3 py-2 text-sm text-white dark:bg-slate-100 dark:text-slate-900">
            {t("projects.create")}
          </button>
          <button type="button" className="px-3 text-sm" onClick={() => setCreating(false)}>
            {t("common.cancel")}
          </button>
        </form>
      )}

      {loaded && projects.length === 0 && !error && (
        <p className="text-sm text-slate-500">
          {admin ? t("projects.emptyAdmin") : t("projects.emptyReader")}
        </p>
      )}

      {projects.length > 0 && (
        <Board>
          {projects.map((p) => (
            <Row
              key={p.id}
              title={
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <Link className="font-semibold hover:underline" to={`/projects/${p.id}`}>{p.name}</Link>
                  {p.visibility === "private" && (
                    <span
                      className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      title={t("projects.privateTitle")}
                    >
                      {t("projects.privateBadge")}
                    </span>
                  )}
                  {p.role && (
                    <span className="text-sm font-normal text-slate-500">
                      {t("projects.yourRoleShort", { role: roleName(p.role) })}
                    </span>
                  )}
                </span>
              }
              sub={
                p.description_md
                  ? <span className="line-clamp-2">{p.description_md.replace(/[#*`>|-]/g, " ").slice(0, 200)}</span>
                  : undefined
              }
              aside={
                <span className="whitespace-nowrap">
                  {t("projects.benchCount", { count: p.bench_count })}
                  {", "}
                  {t("projects.runCount", { count: p.run_count })}
                </span>
              }
            />
          ))}
        </Board>
      )}

    </section>
  );
}

const NO_SELECTIONS: TestSelection[] = [];

/**
 * A product's settings: what it is (its document), who may see and change it,
 * and what its run form offers. Everything set once.
 *
 * This was the product's whole page until the rebuild (step K3, 2026-09-28):
 * the document, two forms, campaigns, the test list, known problems, members
 * and benches, 2157 px on DisplayOS. What somebody opens a product FOR - its
 * newest result, starting a run, its benches - is now the Overview.
 */
export function ProjectDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const pid = Number(id);
  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  // Held here rather than derived at render: the editor resets its rows when
  // this list changes identity, and a fresh array on every render of the page
  // would throw away whatever somebody was typing.
  const [selections, setSelections] = useState<TestSelection[]>(NO_SELECTIONS);
  const [selectionsDefault, setSelectionsDefault] = useState(true);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Named once loaded, numbered until then: a tab that says "Project 3"
  // for a moment beats one that says nothing and then jumps.
  useTitle(project ? project.name : t("projects.fallbackTitle", { id: pid }));

  const load = useCallback(async () => {
    try {
      const d = await api.project(pid);
      setProject(d.project);
      setMembers(d.members ?? []);
      setRole(d.role ?? null);
      setSelections(d.test_selections ?? NO_SELECTIONS);
      setSelectionsDefault(d.test_selections_default !== false);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [pid]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(md: string) {
    setBusy(true);
    try {
      await api.updateProject(pid, { description_md: md });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      {error && <Notice tone="error">{error}</Notice>}
      {!project && !error && <Loading label={t("common.loading")} />}
      {project && (
        <>
          <PageHead
            back={{ to: `/projects/${pid}`, label: t("productSettings.back", { name: project.name }) }}
            title={
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{project.name}</span>
                <Visibility
                  project={project}
                  mayChange={atLeast(role, "owner")}
                  onChanged={load}
                  onError={setError}
                />
                {role && (
                  <span className="text-sm text-slate-500">
                    {t("projects.yourRole", { role: roleName(role) })}
                  </span>
                )}
              </span>
            }
            answer={t("productSettings.answer")}
            actions={
              <>
                {/* Everyone who can read the project may read what it tests. */}
                <Link className="btn-quiet" to={`/projects/${pid}/scenarios`}>
                  {t("projects.scenariosLink")}
                </Link>
                {!editing && atLeast(role, "maintainer") && (
                  <button className="btn-quiet" onClick={() => setEditing(true)}>
                    {t("projects.editDescription")}
                  </button>
                )}
              </>
            }
          />

          <div className="mt-3">
            {editing ? (
              <MarkdownEditor
                label={t("projects.descriptionLabel")}
                value={project.description_md}
                busy={busy}
                onSave={save}
                onCancel={() => setEditing(false)}
              />
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <MarkdownView
                  md={project.description_md}
                  collapsedPx={340}
                  empty={t("projects.noDescription")}
                />
              </div>
            )}
          </div>

          <TestSelectionsEditor
            projectId={pid}
            selections={selections}
            isDefault={selectionsDefault}
            role={role}
            onSaved={load}
          />

          <Members
            scope="project"
            id={pid}
            members={members}
            myRole={role}
            onChanged={load}
          />

        </>
      )}
    </section>
  );
}

/**
 * internal or private, and who may flip it.
 *
 * Spelled out rather than shown as a switch with a label: `internal` means
 * every account there is may read this customer's runs, and that sentence is
 * the decision being made. Only an owner sees the control - a maintainer can
 * configure the rigs and still must not publish the project.
 */
function Visibility({
  project,
  mayChange,
  onChanged,
  onError,
}: {
  project: Project;
  mayChange: boolean;
  onChanged: () => void;
  onError: (s: string) => void;
}) {
  const { t } = useTranslation();
  const priv = project.visibility === "private";
  const label = priv ? t("projects.visPrivate") : t("projects.visInternal");
  if (!mayChange)
    return <span className="text-xs text-slate-500">{label}</span>;
  return (
    <button
      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      onClick={async () => {
        try {
          await api.updateProject(project.id, {
            visibility: priv ? "internal" : "private",
          });
          onChanged();
        } catch (e) {
          onError(e instanceof ApiError ? e.message : String(e));
        }
      }}
    >
      {priv
        ? t("projects.makeInternal", { label })
        : t("projects.makePrivate", { label })}
    </button>
  );
}
